import React, { useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";

import { adicionarParticipante, criarBid, editarBid, listasEscopo } from "services/bidFreteApi";
import StepEscopo from "./wizard/StepEscopo";
import StepPropostaConvidados from "./wizard/StepPropostaConvidados";
import {
  ETAPA_UI,
  NIVEIS_ORDEM,
  NIVEL_UI,
  PESO_PADRAO,
  PRAZO_PROPOSTAS_PADRAO_DIAS,
  PRESETS_ORDEM,
  PRESET_UI,
  complementoPeso,
  ehRfp,
  erroDaApi,
  etapasDoPreset,
  fmtDataISO,
  fmtNumero,
  fmtPeriodo,
  hojeISO,
  montarPayload,
  pesosValidos,
  resumoPrevia,
  ufsDoEscopo,
  validarCriterios,
  validarEscopo,
  validarIdentificacao,
  validarProposta,
} from "./regras";

// Novo BID (H2 PO1-2841, épico PO1-2413). Molde do NovaCotacaoManual
// (STEPS/STEP_META com o trilho wizard-stepper), em modal no padrão dos
// Leilões. Passos: 1 identificação e preset; 2 escopo com prévia; 3 critérios;
// 4 proposta e convidados (só RFP); 5 revisão. Benchmark: POST /gobid/bids ao
// salvar. RFP: o rascunho é gravado ao entrar no passo 4 (as candidatas do
// convite dependem do BID: GET /:id/candidatos-convite); ao salvar, PUT /:id
// com tudo e POST /:id/participantes com as escolhidas. A baseline é criada no
// detalhe; o e-mail do convite só sai ao publicar.

export const STEPS = [
  { id: 1, label: "Identificação" },
  { id: 2, label: "Escopo" },
  { id: 3, label: "Critérios" },
  { id: 4, label: "Proposta e convidados", soRfp: true },
  { id: 5, label: "Revisão" },
];

export const STEP_META = {
  1: { eyebrow: "Preparação", title: "Identificação e modelo", description: "Dê um nome ao BID e escolha entre comparar só as suas tabelas (Benchmark) ou convidar transportadoras (RFP)." },
  2: { eyebrow: "Escopo", title: "NFs do período", description: "Período de emissão, destino e recorte opcional. A prévia conta as NFs e as candidatas antes de gravar." },
  3: { eyebrow: "Ranking", title: "Critérios", description: "Pesos de custo e prazo (somam 100) e o nível em que o ranking abre." },
  4: { eyebrow: "RFP", title: "Proposta e convidados", description: "Cabeçalho da planilha modelo que as transportadoras preenchem e o prazo das propostas." },
  5: { eyebrow: "Revisão", title: "Confira e salve", description: "O BID nasce em rascunho. A baseline congela as NFs e é criada no detalhe." },
};

function prazoPadrao() {
  const d = new Date();
  d.setDate(d.getDate() + PRAZO_PROPOSTAS_PADRAO_DIAS);
  return hojeISO(d);
}

function formInicial() {
  return {
    nome: "",
    preset: "BENCHMARK",
    dataEmissaoDe: "",
    dataEmissaoAte: hojeISO(),
    ufs: [],
    regioes: [],
    transportadorasAtuais: [],
    negocios: [],
    servicos: [],
    modais: [],
    nivelInicial: "BRASIL",
    pesoCusto: PESO_PADRAO.custo,
    pesoPrazo: PESO_PADRAO.prazo,
    tplUfOrigem: "",
    tplIdCidadeOrigem: "",
    tplIdModal: "",
    tplIdNegocio: "",
    tplServico: "",
    tplIdTipoServico: "",
    tplIdFreteUnidade: "",
    prazoPropostas: prazoPadrao(),
    participantes: [],
  };
}

function Badge({ tone, children }) {
  return <span className={`lf-badge lf-badge-${tone || "neutral"}`}>{children}</span>;
}

export default function NovoBidWizard({ isOpen, onClose, empresaId, onCriado }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(formInicial);
  const [erros, setErros] = useState({});
  const [listas, setListas] = useState({ modais: [], servicos: [], estados: [], negocios: [], transportadoras: [], unidades: [] });
  const [loadingListas, setLoadingListas] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroGeral, setErroGeral] = useState(null);
  const [bidId, setBidId] = useState(null);
  const [convidados, setConvidados] = useState({ participantes: [], semEmail: [] });

  useEffect(() => {
    if (!isOpen) return undefined;
    setStep(1);
    setForm(formInicial());
    setErros({});
    setPrevia(null);
    setErroGeral(null);
    setBidId(null);
    setConvidados({ participantes: [], semEmail: [] });
    if (!empresaId) return undefined;
    let ativo = true;
    setLoadingListas(true);
    listasEscopo(empresaId)
      .then((r) => {
        if (ativo) setListas(r);
      })
      .catch(() => {})
      .finally(() => {
        if (ativo) setLoadingListas(false);
      });
    return () => {
      ativo = false;
    };
  }, [isOpen, empresaId]);

  const rfp = ehRfp(form.preset);
  const passos = useMemo(() => STEPS.filter((s) => !s.soRfp || rfp), [rfp]);
  const idxAtual = passos.findIndex((s) => s.id === step);
  const meta = STEP_META[step] || STEP_META[1];

  const onChange = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validarPasso = (id) => {
    if (id === 1) return validarIdentificacao(form);
    if (id === 2) return validarEscopo(form);
    if (id === 3) return validarCriterios(form);
    if (id === 4) return rfp ? validarProposta(form) : {};
    return {};
  };

  // 400 dados_invalidos { errors:{campo:msg} } / 409 nome_duplicado { errors:{ nome } }.
  const tratarErroApi = (err, fallback) => {
    const e = erroDaApi(err, fallback);
    if (e.errors) {
      setErros((cur) => ({ ...cur, ...e.errors, periodo: e.errors.periodo || e.errors.dataEmissaoDe || e.errors.dataEmissaoAte || cur.periodo }));
      if (e.errors.nome) setStep(1);
    }
    setErroGeral(e.errors ? `${e.mensagem} ${Object.values(e.errors).join(" ")}` : e.mensagem);
  };

  // RFP: grava (ou atualiza) o rascunho para o passo 4 listar as candidatas.
  const salvarRascunho = async () => {
    setSalvando(true);
    setErroGeral(null);
    try {
      const payload = montarPayload(form);
      delete payload.participantes;
      const r = bidId ? await editarBid(empresaId, bidId, payload) : await criarBid(empresaId, payload);
      if (r && r.id) setBidId(r.id);
      return true;
    } catch (err) {
      tratarErroApi(err, "Não foi possível salvar o rascunho do BID.");
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const avancar = async () => {
    const e = validarPasso(step);
    setErros(e);
    if (Object.keys(e).length) return;
    const prox = passos[idxAtual + 1];
    if (!prox) return;
    if (prox.id === 4 && rfp) {
      const ok = await salvarRascunho();
      if (!ok) return;
    }
    setStep(prox.id);
  };
  const voltar = () => {
    setErros({});
    const ant = passos[idxAtual - 1];
    if (ant) setStep(ant.id);
  };

  const salvar = async () => {
    const todos = passos.reduce((acc, s) => ({ ...acc, ...validarPasso(s.id) }), {});
    if (Object.keys(todos).length) {
      setErros(todos);
      const primeiro = passos.find((s) => Object.keys(validarPasso(s.id)).length);
      if (primeiro) setStep(primeiro.id);
      return;
    }
    if (rfp && convidados.semEmail.length) {
      setErros({ convidados: `Informe o e-mail de: ${convidados.semEmail.join(", ")}.` });
      setStep(4);
      return;
    }
    setSalvando(true);
    setErroGeral(null);
    try {
      const payload = montarPayload(form);
      delete payload.participantes;
      let bid;
      if (bidId) bid = await editarBid(empresaId, bidId, payload);
      else bid = await criarBid(empresaId, payload);
      const id = (bid && bid.id) || bidId;
      if (rfp && convidados.participantes.length) {
        try {
          await adicionarParticipante(empresaId, id, convidados.participantes);
        } catch (err) {
          // O BID já está salvo: avisa e segue para o detalhe (dá para convidar na aba Convites).
          const e = erroDaApi(err, "Não foi possível incluir as convidadas.");
          if (onCriado) onCriado({ ...(bid || {}), id, avisoConvidados: e.mensagem });
          return;
        }
      }
      if (onCriado) onCriado({ ...(bid || {}), id });
    } catch (err) {
      tratarErroApi(err, "Não foi possível salvar o BID.");
    } finally {
      setSalvando(false);
    }
  };

  const p = resumoPrevia(previa);
  const ufsEfetivas = ufsDoEscopo(
    (form.ufs || []).map((o) => o.value),
    (form.regioes || []).map((o) => o.value),
  );
  const nomeDe = (lista) => (lista || []).map((o) => o.label).join(", ") || "Todos";
  const ultimo = idxAtual === passos.length - 1;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" modalClassName="modal-align-top" contentClassName="lf-modal bf-wizard" backdrop="static" fade={false}>
      <div className="lf-head">
        <span className="lf-head-title">
          <span>{bidId ? `Novo BID de Frete · rascunho #${bidId}` : "Novo BID de Frete"}</span>
          <Badge tone={rfp ? "info" : "neutral"}>{PRESET_UI[form.preset] ? PRESET_UI[form.preset].label : ""}</Badge>
        </span>
        <button type="button" className="lf-head-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>
      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <div className="bf-wizard-stepper" data-testid="bf-stepper">
          {passos.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className={`bf-wizard-step${step === s.id ? " active" : ""}${idxAtual > i ? " done" : ""}`}>
                <div className="bf-wizard-step-circle">{idxAtual > i ? "✓" : i + 1}</div>
                <span className="bf-wizard-step-label">{s.label}</span>
              </div>
              {i < passos.length - 1 ? <div className={`bf-wizard-line${idxAtual > i ? " done" : ""}`} /> : null}
            </React.Fragment>
          ))}
        </div>

        <div className="bf-step-head">
          <div className="bf-step-eyebrow">{meta.eyebrow}</div>
          <div className="bf-step-title">{meta.title}</div>
          <p className="bf-step-desc">{meta.description}</p>
        </div>

        {step === 1 ? (
          <>
            <FormGroup>
              <Label>Nome do BID *</Label>
              <Input
                type="text"
                maxLength={100}
                value={form.nome}
                placeholder="Ex.: Benchmark Sudeste · set/2026"
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({ nome: v });
                }}
              />
              {erros.nome ? <div className="lf-erro-campo">{erros.nome}</div> : null}
            </FormGroup>
            <Label>Modelo *</Label>
            <div className="bf-presets">
              {PRESETS_ORDEM.map((k) => {
                const pu = PRESET_UI[k];
                return (
                  <button key={k} type="button" className={`bf-preset${form.preset === k ? " is-active" : ""}`} onClick={() => onChange({ preset: k })} aria-pressed={form.preset === k} disabled={Boolean(bidId)} title={bidId ? "O modelo não muda depois do rascunho salvo." : undefined}>
                    <div className="bf-preset-title">{pu.label}</div>
                    <div className="bf-preset-desc">{pu.descricao}</div>
                    <div className="bf-preset-etapas">
                      {pu.etapas.map((et) => (
                        <Badge key={et} tone="neutral">
                          {ETAPA_UI[et].label}
                        </Badge>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
            {erros.preset ? <div className="lf-erro-campo">{erros.preset}</div> : null}
          </>
        ) : null}

        {step === 2 ? <StepEscopo form={form} onChange={onChange} listas={listas} loadingListas={loadingListas} empresaId={empresaId} erros={erros} previa={previa} onPrevia={setPrevia} /> : null}

        {step === 3 ? (
          <>
            <div className="lf-section">
              <div className="lf-section-title">Pesos do ranking</div>
              <div className="bf-pesos">
                <FormGroup>
                  <Label>Custo (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={form.pesoCusto}
                    onChange={(e) => {
                      const v = e.target.value;
                      const n = v === "" ? "" : Number(v);
                      onChange({ pesoCusto: n, pesoPrazo: v === "" ? form.pesoPrazo : complementoPeso(n) });
                    }}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Prazo (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={form.pesoPrazo}
                    onChange={(e) => {
                      const v = e.target.value;
                      const n = v === "" ? "" : Number(v);
                      onChange({ pesoPrazo: n, pesoCusto: v === "" ? form.pesoCusto : complementoPeso(n) });
                    }}
                  />
                </FormGroup>
              </div>
              <div className={`bf-pesos-soma${pesosValidos(form.pesoCusto, form.pesoPrazo) ? "" : " is-erro"}`}>
                Soma: {Number(form.pesoCusto) + Number(form.pesoPrazo) || 0} de 100. Score = custo × (menor frete ÷ frete) + prazo × (menor prazo ÷ prazo). Candidata sem prazo usa só o custo.
              </div>
              {erros.pesos ? <div className="lf-erro-campo">{erros.pesos}</div> : null}
            </div>
            <div className="lf-section">
              <div className="lf-section-title">Nível inicial do ranking</div>
              <div className="lf-presets">
                {NIVEIS_ORDEM.map((n) => (
                  <button key={n} type="button" className={`lf-preset${form.nivelInicial === n ? " is-active" : ""}`} onClick={() => onChange({ nivelInicial: n })}>
                    {NIVEL_UI[n].label}
                  </button>
                ))}
              </div>
              <div className="lf-muted">O ranking é gravado em todos os níveis (Brasil, cada região, cada UF e cada modal); isto define só a visão de abertura.</div>
              {erros.nivelInicial ? <div className="lf-erro-campo">{erros.nivelInicial}</div> : null}
            </div>
          </>
        ) : null}

        {step === 4 && rfp ? <StepPropostaConvidados form={form} onChange={onChange} listas={listas} erros={erros} empresaId={empresaId} bidId={bidId} onConvidados={setConvidados} /> : null}

        {step === 5 ? (
          <>
            <div className="lf-section">
              <div className="lf-section-title">Identificação</div>
              <div className="lf-kv">
                <div>
                  <div className="lf-kv-label">Nome</div>
                  <div className="lf-kv-value">{form.nome}</div>
                </div>
                <div>
                  <div className="lf-kv-label">Modelo</div>
                  <div className="lf-kv-value">{PRESET_UI[form.preset].label}</div>
                </div>
                <div>
                  <div className="lf-kv-label">Etapas</div>
                  <div className="lf-kv-value">{etapasDoPreset(form.preset).map((e) => ETAPA_UI[e].label).join(" → ")}</div>
                </div>
              </div>
            </div>
            <div className="lf-section">
              <div className="lf-section-title">Escopo</div>
              <div className="lf-kv">
                <div>
                  <div className="lf-kv-label">Período de emissão</div>
                  <div className="lf-kv-value">{fmtPeriodo(form.dataEmissaoDe, form.dataEmissaoAte)}</div>
                </div>
                <div>
                  <div className="lf-kv-label">Destino</div>
                  <div className="lf-kv-value">{ufsEfetivas.join(", ") || "—"}</div>
                </div>
                <div>
                  <div className="lf-kv-label">Transportadora atual</div>
                  <div className="lf-kv-value">{nomeDe(form.transportadorasAtuais)}</div>
                </div>
                <div>
                  <div className="lf-kv-label">Negócio</div>
                  <div className="lf-kv-value">{nomeDe(form.negocios)}</div>
                </div>
                <div>
                  <div className="lf-kv-label">Serviço</div>
                  <div className="lf-kv-value">{nomeDe(form.servicos)}</div>
                </div>
                <div>
                  <div className="lf-kv-label">Modal</div>
                  <div className="lf-kv-value">{nomeDe(form.modais)}</div>
                </div>
              </div>
              {p ? (
                <div className="lf-muted" style={{ marginTop: 8 }}>
                  Prévia: {fmtNumero(p.qtdNf)} NFs · {fmtNumero(p.qtdComCte)} com CTe · {fmtNumero(p.candidatasTotal)} candidatas · ~{fmtNumero(p.estimativaCalculos)} cálculos
                  {p.estimativaMinutos != null ? ` (~${fmtNumero(p.estimativaMinutos)} min)` : ""}
                </div>
              ) : null}
            </div>
            <div className="lf-section">
              <div className="lf-section-title">Critérios</div>
              <div className="lf-kv">
                <div>
                  <div className="lf-kv-label">Pesos</div>
                  <div className="lf-kv-value">
                    custo {form.pesoCusto}% · prazo {form.pesoPrazo}%
                  </div>
                </div>
                <div>
                  <div className="lf-kv-label">Nível inicial</div>
                  <div className="lf-kv-value">{NIVEL_UI[form.nivelInicial] ? NIVEL_UI[form.nivelInicial].label : "—"}</div>
                </div>
              </div>
            </div>
            {rfp ? (
              <div className="lf-section">
                <div className="lf-section-title">Proposta</div>
                <div className="lf-kv">
                  <div>
                    <div className="lf-kv-label">Prazo das propostas</div>
                    <div className="lf-kv-value">{fmtDataISO(form.prazoPropostas)}</div>
                  </div>
                  <div>
                    <div className="lf-kv-label">Modal / unidade</div>
                    <div className="lf-kv-value">
                      {(listas.modais.find((m) => String(m.id) === String(form.tplIdModal)) || {}).nome || "—"} /{" "}
                      {(listas.unidades.find((u) => String(u.id) === String(form.tplIdFreteUnidade)) || {}).nome || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="lf-kv-label">Convidadas</div>
                    <div className="lf-kv-value">{convidados.participantes.length ? `${convidados.participantes.length} selecionada(s); o e-mail sai ao publicar` : "nenhuma ainda (inclua na aba Convites)"}</div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {erroGeral ? <div className="lf-aviso lf-aviso-danger">{erroGeral}</div> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" className="btn-sm" onClick={onClose} disabled={salvando}>
          Cancelar
        </Button>
        {idxAtual > 0 ? (
          <Button color="secondary" className="btn-sm" outline onClick={voltar} disabled={salvando}>
            Voltar
          </Button>
        ) : null}
        {ultimo ? (
          <Button color="primary" className="btn-sm" onClick={salvar} disabled={salvando}>
            {salvando ? <Spinner size="sm" /> : "Salvar BID"}
          </Button>
        ) : (
          <Button color="primary" className="btn-sm" onClick={avancar} disabled={salvando}>
            {salvando ? <Spinner size="sm" /> : "Avançar"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
